<template>
<div>
    <div class="mb-3">
        <b-button v-if="canAdd" @click="toggleAdd" :variant="addNew ? 'danger' : 'primary'">
            <i :class="addNew ? 'fa-solid fa-ban' : 'fa-solid fa-plus'"></i> {{ addNew ? 'Cancel' : 'Add visit' }}
        </b-button>
        <b-collapse v-model="addNew" class="mt-3">
            <b-card>
                <validation-observer ref="observer" v-slot="{ passes }">
                <b-form @submit.prevent="passes(onSubmit)" novalidate>
                    <b-row>
                        <b-col md="4">
                            <validation-provider vid="visit_date" v-slot="{ errors }">
                                <b-form-group label="Visit date" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-datepicker v-model="form.visit_date" :locale="currentLang" :max="today" reset-button placeholder="Date..." />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="4">
                            <validation-provider vid="gestational_age" v-slot="{ errors }">
                                <b-form-group label="Gestational age" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input v-model="form.gestational_age" placeholder="e.g. 28w+3d" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="4">
                            <validation-provider vid="fundal_height" v-slot="{ errors }">
                                <b-form-group label="Fundal height" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input v-model="form.fundal_height" placeholder="cm" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="3">
                            <validation-provider vid="bp_systolic" v-slot="{ errors }">
                                <b-form-group label="BP systolic" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" min="0" v-model.number="form.bp_systolic" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="bp_diastolic" v-slot="{ errors }">
                                <b-form-group label="BP diastolic" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" min="0" v-model.number="form.bp_diastolic" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="fetal_heart_rate" v-slot="{ errors }">
                                <b-form-group label="FHR (bpm)" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" min="0" v-model.number="form.fetal_heart_rate" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="hemoglobin" v-slot="{ errors }">
                                <b-form-group label="Hb (mmol/L)" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" step="0.01" min="0" v-model.number="form.hemoglobin" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="4">
                            <validation-provider vid="fetal_position" v-slot="{ errors }">
                                <b-form-group label="Fetal position" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input v-model="form.fetal_position" placeholder="e.g. cephalic" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="4">
                            <validation-provider vid="edema" v-slot="{ errors }">
                                <b-form-group label="Edema" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-select plain v-model="form.edema">
                                        <b-form-select-option :value="null">n/a</b-form-select-option>
                                        <b-form-select-option v-for="o in edemaOptions" :key="o.value" :value="o.value">{{ o.text }}</b-form-select-option>
                                    </b-form-select>
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="4">
                            <validation-provider vid="kb" v-slot="{ errors }">
                                <b-form-group label="Fetal movements" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-select plain v-model="form.kb">
                                        <b-form-select-option :value="null">n/a</b-form-select-option>
                                        <b-form-select-option v-for="o in kbOptions" :key="o.value" :value="o.value">{{ o.text }}</b-form-select-option>
                                    </b-form-select>
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="4">
                            <validation-provider vid="maternal_weight" v-slot="{ errors }">
                                <b-form-group label="Maternal weight (kg)" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" step="0.01" min="0" v-model.number="form.maternal_weight" placeholder="kg" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="4">
                            <validation-provider vid="estimated_fetal_weight" v-slot="{ errors }">
                                <b-form-group label="Est. fetal weight" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input v-model="form.estimated_fetal_weight" placeholder="g" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <validation-provider vid="plan_therapy" v-slot="{ errors }">
                        <b-form-group label="Plan / therapy" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                            <b-form-textarea rows="2" max-rows="6" v-model="form.plan_therapy" placeholder="Plan..." />
                        </b-form-group>
                    </validation-provider>

                    <b-button class="mr-1" type="submit" variant="success"><i class="fa-regular fa-circle-dot"></i> {{ editingId ? 'Save' : 'Submit' }}</b-button>
                    <b-button v-if="isDirty || hasErrors" type="button" variant="danger" @click="resetForm"><i class="fa-solid fa-ban"></i> Reset</b-button>
                </b-form>
                </validation-observer>
            </b-card>
        </b-collapse>
    </div>

    <b-overlay :show="confirm" no-wrap @shown="onShown">
        <template #overlay>
            <div ref="dialog" tabindex="-1" role="dialog" aria-modal="false">
                <p class="text-center">
                    <i class="fa-light fa-xmark text-danger"></i> <strong class="text-danger">DELETE antenatal visit?</strong>
                </p>
                <div class="text-center">
                    <b-button variant="outline-danger" class="mr-3" @click="onCancel">Cancel</b-button>
                    <b-button variant="outline-success" @click="onConfirmed(selected)">Confirm</b-button>
                </div>
            </div>
        </template>
    </b-overlay>

    <b-table
        responsive
        stacked="sm"
        show-empty
        striped
        hover
        small
        primary-key="id"
        :current-page="currentPage"
        :per-page="perPage"
        :items="items"
        :fields="fields"
        :sort-by.sync="sortedBy"
        :sort-desc.sync="sortedDesc">

        <template v-slot:cell(visit_date)="data">
            <span :class="{ 'small font-italic text-muted': !data.value }">{{ data.value ? toLocaleDate(data.value) : 'n/a' }}</span>
        </template>
        <template v-slot:cell(blood_pressure)="data">
            <span :class="{ 'small font-italic text-muted': !data.value, 'text-danger font-weight-bold': isHighBp(data.item) }">{{ data.value || 'n/a' }}</span>
        </template>
        <template v-slot:cell(fetal_heart_rate)="data">
            <span :class="{ 'small font-italic text-muted': !data.value, 'text-danger font-weight-bold': isAbnormalFhr(data.value) }">{{ data.value || 'n/a' }}</span>
        </template>
        <template v-slot:cell(maternal_weight)="data">
            <span :class="{ 'small font-italic text-muted': !data.value }">{{ data.value ? `${data.value} kg` : 'n/a' }}</span>
        </template>
        <template v-slot:cell(actions)="row">
            <b-button v-if="canChange" size="sm" variant="outline-primary" class="mr-1" @click="onEdit(row.item)"><i class="fa-light fa-pen"></i></b-button>
            <b-button v-if="canDelete" size="sm" variant="danger" @click="onRemove(row.item)"><i class="fa-light fa-xmark"></i></b-button>
        </template>
        <template v-slot:empty>
            <div class="small text-muted text-center my-2"><span>No antenatal visits recorded</span></div>
        </template>
    </b-table>
    <b-pagination v-if="totalRows > perPage" class="m-0" align="right" :total-rows="totalRows" v-model="currentPage" :per-page="perPage" />
</div>
</template>
<script>
import FormExtension from "@/core/components/FormExtension"
import { mapActions } from "vuex"

export default FormExtension.extend({
    name: "antepartum-visit-list",
    props: {
        record: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            today: new Date(),
            addNew: false,
            editingId: null,
            confirm: false,
            selected: null,
            currentPage: 1,
            perPage: 5,
            sortedBy: 'visit_date',
            sortedDesc: true,
            edemaOptions: [
                { value: '-', text: 'None (–)' },
                { value: '+', text: 'Mild (+)' },
                { value: '++', text: 'Moderate (++)' },
                { value: '+++', text: 'Severe (+++)' },
                { value: '++++', text: 'Very severe (++++)' },
            ],
            kbOptions: [
                { value: '+', text: 'Felt (+)' },
                { value: '-', text: 'Not felt (–)' },
                { value: 'reduced', text: 'Reduced' },
            ],
            form: new Form({
                visit_date: null,
                gestational_age: "",
                fundal_height: "",
                fetal_position: "",
                maternal_weight: null,
                edema: null,
                fetal_heart_rate: null,
                kb: null,
                estimated_fetal_weight: "",
                bp_systolic: null,
                bp_diastolic: null,
                hemoglobin: null,
                plan_therapy: "",
                consistency_token: null,
            }),
            fields: [
                { key: 'visit_date', label: 'Date', class: 'align-middle' },
                { key: 'gestational_age', label: 'GA', class: 'align-middle' },
                { key: 'blood_pressure', label: 'BP', class: 'align-middle' },
                { key: 'fetal_heart_rate', label: 'FHR', class: 'align-middle' },
                { key: 'fundal_height', label: 'FH', class: 'align-middle' },
                { key: 'maternal_weight', label: 'Gew', class: 'align-middle' },
                { key: 'hemoglobin', label: 'Hb', class: 'align-middle' },
                { key: 'actions', label: '', class: 'align-middle text-right' },
            ],
        }
    },
    computed: {
        items() {
            return this.record?.visits || []
        },
        totalRows() {
            return this.items.length
        },
        canAdd() {
            return this.checkUserPermission('maternity_visit', 'can_add')
        },
        canChange() {
            return this.checkUserPermission('maternity_visit', 'can_change')
        },
        canDelete() {
            return this.checkUserPermission('maternity_visit', 'can_delete')
        },
    },
    watch: {
        form: {
            deep: true,
            handler() {
                this.isDirty = this.form.dirty()
                if (this.isDirty) {
                    this.dirty()
                } else {
                    this.validated()
                }
            },
        },
    },
    methods: {
        ...mapActions('maternity', ['createVisit', 'updateVisit', 'deleteVisit']),
        isHighBp(item) {
            return (item.bp_systolic && item.bp_systolic >= 140) || (item.bp_diastolic && item.bp_diastolic >= 90)
        },
        isAbnormalFhr(value) {
            return value && (value < 110 || value > 160)
        },
        toggleAdd() {
            this.addNew = !this.addNew
            if (!this.addNew) {
                this.resetForm()
            }
        },
        onEdit(item) {
            this.editingId = item.id
            this.form.withData(item)
            this.addNew = true
        },
        onSubmit() {
            const payload = { ...this.form.data(), record: this.record.id }
            const action = this.editingId
                ? this.updateVisit({ ...payload, id: this.editingId })
                : this.createVisit(payload)

            action
                .then(() => {
                    this.notify("Saved", "Antenatal visit saved successfully")
                    this.resetForm()
                    this.addNew = false
                })
                .catch(errors => {
                    if (errors.response) {
                        this.$refs.observer?.setErrors(errors.response.data)
                        this.hasErrors = true
                    }
                })
        },
        resetForm() {
            this.editingId = null
            this.form.reset()
            this.$nextTick(() => {
                this.$refs.observer?.reset()
                this.hasErrors = false
                this.validated()
            })
        },
        onRemove(item) {
            this.confirm = true
            this.selected = item
        },
        onConfirmed(item) {
            this.deleteVisit(item.id)
                .then(() => {
                    this.notify("Removed", "Antenatal visit removed successfully")
                    this.confirm = false
                    this.selected = null
                })
                .catch(() => {
                    this.notify("Error", "Something went wrong", 'danger')
                    this.confirm = false
                    this.selected = null
                })
        },
        onShown() {
            this.$refs.dialog?.focus()
        },
        onCancel() {
            this.confirm = false
            this.selected = null
        },
    },
})
</script>
